/* Mock routes — marketplace (nursery partners, products). */

import type { Nursery, Paged, Product } from '@core/types'
import { ApiError } from '../../http'
import { db } from '../store'
import { paginate, readQueryNumber, type MockRoute } from '../types'

export const marketplaceRoutes: MockRoute[] = [
  {
    method: 'GET',
    pattern: '/marketplace/products',
    handler: ({ query }) => {
      const category = query.get('category')
      const governorate = query.get('governorate')
      const nurseryId = query.get('nurseryId')
      const search = query.get('search')?.toLowerCase()
      const maxPrice = readQueryNumber(query, 'maxPriceIqd')
      const inStockOnly = query.get('inStockOnly') === 'true'
      const sort = query.get('sort') ?? 'relevance'
      const page = readQueryNumber(query, 'page') ?? 1
      const pageSize = readQueryNumber(query, 'pageSize') ?? 9

      let products = [...db.products]
      if (category && category !== 'all') {
        products = products.filter((product) => product.category === category)
      }
      if (nurseryId) products = products.filter((product) => product.nursery.id === nurseryId)
      if (governorate && governorate !== 'all') {
        products = products.filter((product) => product.nursery.governorate === governorate)
      }
      if (search) {
        products = products.filter(
          (product) =>
            product.name.toLowerCase().includes(search) ||
            product.nameAr.includes(search) ||
            product.description.toLowerCase().includes(search),
        )
      }
      if (maxPrice !== undefined) {
        products = products.filter((product) => product.priceIqd <= maxPrice)
      }
      if (inStockOnly) products = products.filter((product) => product.stock > 0)

      products.sort((a, b) => {
        if (sort === 'price-asc') return a.priceIqd - b.priceIqd
        if (sort === 'price-desc') return b.priceIqd - a.priceIqd
        if (sort === 'rating') return b.nursery.name.localeCompare(a.nursery.name)
        // sponsored placements come first, then verified nurseries, then stock
        return (
          Number(b.sponsored) - Number(a.sponsored) ||
          Number(b.nursery.verified) - Number(a.nursery.verified) ||
          b.stock - a.stock
        )
      })

      return paginate(products, page, pageSize) satisfies Paged<Product>
    },
    emptyState: {
      items: [],
      total: 0,
      page: 1,
      pageSize: 9,
    } satisfies Paged<Product>,
  },
  {
    method: 'GET',
    pattern: '/marketplace/products/:id',
    handler: ({ params }) => {
      const product = db.products.find((item) => item.id === params.id)
      if (!product) {
        throw new ApiError({
          status: 404,
          code: 'product_not_found',
          message: 'That product has been removed from the marketplace.',
        })
      }
      return product
    },
  },
  {
    method: 'GET',
    pattern: '/marketplace/nurseries',
    handler: ({ query }) => {
      const governorate = query.get('governorate')
      const verifiedOnly = query.get('verifiedOnly') === 'true'
      let nurseries = [...db.nurseries]
      if (governorate && governorate !== 'all') {
        nurseries = nurseries.filter((nursery) => nursery.governorate === governorate)
      }
      if (verifiedOnly) nurseries = nurseries.filter((nursery) => nursery.verified)
      return nurseries.sort((a, b) => b.rating - a.rating)
    },
    emptyState: [] satisfies Nursery[],
  },
]
